import { Activity, Database, RefreshCw, ShieldCheck, Trash2 } from "lucide-react";

const formatDateTime = (value) => {
        if (!value) return "—";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return "—";
        return new Intl.DateTimeFormat("pt-PT", {
                dateStyle: "short",
                timeStyle: "short",
        }).format(date);
};

const formatNumber = (value) => new Intl.NumberFormat("pt-PT").format(value || 0);

const statusColor = (status) => {
        if (["ok", "completed", "published"].includes(status)) return "var(--truth)";
        if (["running", "draft", "review", "degraded"].includes(status)) return "var(--ai)";
        if (["failed", "fail", "blocked", "archived"].includes(status)) return "var(--deception)";
        return "var(--ink-soft)";
};

export const AdminOperationsPanel = ({
        auditLog,
        cleaningUploads,
        loadingOps,
        onCleanupUploads,
        onRefresh,
        opsJobs,
        opsStatus,
        opsUsage,
        uploadCleanupResult,
}) => {
        const usageByKind = opsUsage?.by_kind || {};
        const recentJobs = opsJobs.slice(0, 6);
        const recentAudit = auditLog.slice(0, 8);
        const recentWebsets = opsStatus?.operations?.recent_websets || [];
        const searchStatus = opsStatus?.search || {};

        return (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
                        <section className="adm-card p-6">
                                <div className="flex items-start justify-between gap-4 mb-6">
                                        <div>
                                                <div className="flex items-center gap-2 overline text-[var(--ai)] mb-2">
                                                        <Activity size={15} strokeWidth={1.5} />
                                                        Operação
                                                </div>
                                                <p className="font-serif text-2xl text-[var(--ink)] leading-tight">
                                                        Jobs e uso das últimas 24h
                                                </p>
                                        </div>
                                        <button
                                                type="button"
                                                onClick={onRefresh}
                                                disabled={loadingOps}
                                                className="min-h-10 min-w-10 inline-flex items-center justify-center border border-[var(--line-strong)] hover:border-[var(--ai)] hover:text-[var(--ai)] transition-colors disabled:opacity-50"
                                                title="Atualizar operação"
                                        >
                                                <RefreshCw size={15} strokeWidth={1.5} className={loadingOps ? "animate-spin" : ""} />
                                        </button>
                                </div>
                                <div className="grid grid-cols-2 lg:grid-cols-4 border border-[var(--line)] mb-6">
                                        {[
                                                ["Serviço", opsStatus?.service?.status || "—"],
                                                ["Base dados", opsStatus?.database?.status || "—"],
                                                ["Eventos 24h", formatNumber(opsUsage?.total_events || 0)],
                                                ["Jobs", formatNumber(opsJobs.length)],
                                        ].map(([label, value]) => (
                                                <div key={label} className="p-4 border-r border-[var(--line)] last:border-r-0">
                                                        <div className="overline text-[var(--ink-faint)] mb-2">{label}</div>
                                                        <div className="font-mono text-sm" style={{ color: statusColor(value) }}>{value}</div>
                                                </div>
                                        ))}
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
                                        {["llm", "search", "extract", "rss"].map((kind) => {
                                                const item = usageByKind[kind] || {};
                                                return (
                                                        <div key={kind} className="border border-[var(--line)] p-4">
                                                                <div className="overline text-[var(--ink-soft)] mb-2">{kind}</div>
                                                                <div className="font-serif text-2xl text-[var(--ink)]">{formatNumber(item.events)}</div>
                                                                <div className="text-xs font-mono text-[var(--ink-faint)]">
                                                                        {formatNumber(item.units)} unidades · {formatNumber(item.fail)} falhas
                                                                </div>
                                                        </div>
                                                );
                                        })}
                                </div>
                                <div className="border border-[var(--line)] p-4 mb-6">
                                        <div className="overline text-[var(--ink-soft)] mb-2">Pesquisa profunda</div>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs font-mono text-[var(--ink-soft)]">
                                                <div>extract: {searchStatus.infinitum_extract_enabled ? "ativo" : "manual off"}</div>
                                                <div>auto-discover: {searchStatus.infinitum_extract_auto_discover_enabled ? "ativo" : "off"}</div>
                                                <div>máx. URLs: {searchStatus.infinitum_extract_max_urls || "—"}</div>
                                        </div>
                                </div>
                                <div className="border border-[var(--line)] p-4 mb-6">
                                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                                                <div>
                                                        <div className="overline text-[var(--ink-soft)] mb-2">Imagens locais</div>
                                                        <p className="text-xs font-mono text-[var(--ink-soft)] leading-relaxed">
                                                                Remove apenas ficheiros em uploads que já não estejam ligados a nenhum dossiê.
                                                        </p>
                                                        {uploadCleanupResult && (
                                                                <p className="mt-2 text-xs font-mono text-[var(--ink-faint)]">
                                                                        {formatNumber(uploadCleanupResult.deleted)} apagadas · {formatNumber(uploadCleanupResult.skipped_referenced)} em uso · {formatNumber(uploadCleanupResult.skipped_young)} recentes
                                                                </p>
                                                        )}
                                                </div>
                                                <button
                                                        type="button"
                                                        onClick={onCleanupUploads}
                                                        disabled={cleaningUploads}
                                                        className="adm-btn adm-btn-danger"
                                                >
                                                        {cleaningUploads ? (
                                                                <RefreshCw size={14} strokeWidth={1.5} className="animate-spin" />
                                                        ) : (
                                                                <Trash2 size={14} strokeWidth={1.5} />
                                                        )}
                                                        Limpar órfãs
                                                </button>
                                        </div>
                                </div>
                                <div className="space-y-2">
                                        {recentJobs.map((job) => (
                                                <div key={job.id} className="border border-[var(--line)] p-3 flex items-start justify-between gap-3">
                                                        <div>
                                                                <div className="overline" style={{ color: statusColor(job.status) }}>{job.job_type} · {job.status}</div>
                                                                <p className="text-xs font-mono text-[var(--ink-soft)] mt-1">
                                                                        {job.source} · {formatDateTime(job.started_at)}
                                                                </p>
                                                        </div>
                                                        <div className="text-right text-xs font-mono text-[var(--ink-faint)]">
                                                                {job.finished_at ? formatDateTime(job.finished_at) : "em curso"}
                                                        </div>
                                                </div>
                                        ))}
                                        {recentJobs.length === 0 && (
                                                <div className="border border-dashed border-[var(--line)] p-4 text-xs font-mono text-[var(--ink-faint)]">
                                                        Sem jobs registados.
                                                </div>
                                        )}
                                </div>
                        </section>

                        <section className="adm-card p-6">
                                <div className="flex items-center gap-2 overline text-[var(--truth)] mb-2">
                                        <ShieldCheck size={15} strokeWidth={1.5} />
                                        Auditoria
                                </div>
                                <p className="font-serif text-2xl text-[var(--ink)] leading-tight mb-6">
                                        Últimas ações administrativas
                                </p>
                                <div className="space-y-2">
                                        {recentAudit.map((item) => (
                                                <div key={item.id} className="border border-[var(--line)] p-3">
                                                        <div className="flex items-start justify-between gap-3">
                                                                <div>
                                                                        <div className="overline text-[var(--ink)]">{item.action}</div>
                                                                        <p className="text-xs font-mono text-[var(--ink-soft)] mt-1">
                                                                                {item.actor} {item.target ? `· ${item.target}` : ""}
                                                                        </p>
                                                                </div>
                                                                <div className="text-xs font-mono text-[var(--ink-faint)] whitespace-nowrap">
                                                                        {formatDateTime(item.created_at)}
                                                                </div>
                                                        </div>
                                                </div>
                                        ))}
                                        {recentAudit.length === 0 && (
                                                <div className="border border-dashed border-[var(--line)] p-4 text-xs font-mono text-[var(--ink-faint)]">
                                                        Sem auditoria recente.
                                                </div>
                                        )}
                                </div>
                                <div className="mt-8 pt-6 border-t border-[var(--line-strong)]">
                                        <div className="flex items-center gap-2 overline text-[var(--ai)] mb-2">
                                                <Database size={15} strokeWidth={1.5} />
                                                Websets
                                        </div>
                                        <p className="font-serif text-xl text-[var(--ink)] leading-tight mb-4">
                                                Memória recente de investigação
                                        </p>
                                        <div className="space-y-2">
                                                {recentWebsets.map((webset) => (
                                                        <div key={webset.id} className="border border-[var(--line)] p-3">
                                                                <div className="overline text-[var(--ink)]">
                                                                        {webset.operation || "investigação"}
                                                                </div>
                                                                <p className="text-xs font-mono text-[var(--ink-soft)] mt-1 line-clamp-2">
                                                                        {webset.research_query || "sem query"}
                                                                </p>
                                                                <div className="text-[11px] font-mono text-[var(--ink-faint)] mt-2">
                                                                        {(webset.sources || []).length} fontes · {(webset.evidence || []).length} extrações · {formatDateTime(webset.created_at)}
                                                                </div>
                                                        </div>
                                                ))}
                                                {recentWebsets.length === 0 && (
                                                        <div className="border border-dashed border-[var(--line)] p-4 text-xs font-mono text-[var(--ink-faint)]">
                                                                Ainda não há websets guardados.
                                                        </div>
                                                )}
                                        </div>
                                </div>
                        </section>
                </div>
        );
};
